import {
  Autocomplete,
  AutocompleteProps,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { CatalogProduct, CatalogProductQuery } from '../types'
import { useEffect, useState } from 'react'
import { getCatalogProducts } from '../services/catalogProductService'
import { useTranslation } from 'react-i18next'
import { Circle } from '@mui/icons-material'
import { CatalogProductStatus, CatalogProductType } from '../enums'
import CatalogProductDetailsList from './CatalogProductDetailsList'

type CatalogProductGroupBy = 'manufacturer' | 'fuse' | 'heatingPower'

export interface CatalogProductAutocompleteProps extends Omit<
  AutocompleteProps<CatalogProduct, false, false, false>,
  'options' | 'value' | 'renderInput' | 'groupBy'
> {
  type?: CatalogProductType
  query?: CatalogProductQuery
  value?: string | null
  label?: string
  placeholder?: string
  options?: CatalogProduct[]
  groupBy?: CatalogProductGroupBy
}

export default function CatalogProductAutocomplete({
  type,
  query = {},
  value,
  label,
  placeholder,
  options,
  groupBy,
  inputValue,
  ...props
}: CatalogProductAutocompleteProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The catalog products state.
   */
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([])

  const selectedCatalogProduct = catalogProducts.find((product) => product.id === value) ?? null

  /**
   * The sorted options.
   */
  const sortedOptions = sortOptions(catalogProducts)

  /**
   * Sort the options.
   */
  function sortOptions(options: CatalogProduct[]) {
    return [...options].sort((a, b) => {
      if (groupBy === 'manufacturer') {
        return a.manufacturer.localeCompare(b.manufacturer)
      } else if (groupBy === 'fuse') {
        return (a.customData?.fuse ?? 0) - (b.customData?.fuse ?? 0)
      } else if (groupBy === 'heatingPower') {
        return (a.customData?.heatingPower ?? 0) - (b.customData?.heatingPower ?? 0)
      }

      return a.name.localeCompare(b.name)
    })
  }

  function groupByOption(option: CatalogProduct) {
    if (groupBy === 'fuse') {
      return `${option?.customData?.fuse}A`
    } else if (groupBy === 'heatingPower') {
      return `${option?.customData?.heatingPower} kW`
    } else {
      return option.manufacturer
    }
  }

  /**
   * Load the catalog products.
   */
  async function loadCatalogProducts(query: CatalogProductQuery, controller?: AbortController) {
    try {
      setCatalogProducts(await getCatalogProducts(query, { signal: controller?.signal }))
    } catch (_) {
      // Do nothing.
    }
  }

  /**
   * Load the catalog products when the component mounts unless options are provided.
   */
  useEffect(() => {
    if (options === undefined) {
      const controller = new AbortController()

      loadCatalogProducts(
        {
          status: CatalogProductStatus.VERIFIED,
          type,
          ...query
        },
        controller
      )

      return () => controller.abort()
    }
  }, [options, type, query])

  return (
    <>
      <Autocomplete
        options={sortedOptions}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
        noOptionsText={null}
        groupBy={groupBy ? groupByOption : undefined}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => (
          <MenuItem {...props}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography>
                {option.manufacturerProductCode
                  ? `${option.name} (${option.manufacturerProductCode})`
                  : option.name}
              </Typography>

              {option.manufacturer && (
                <>
                  <Circle color="disabled" sx={{ fontSize: '4px' }} />
                  <Typography color="text.secondary">{option.manufacturer}</Typography>
                </>
              )}
            </Stack>
          </MenuItem>
        )}
        renderInput={(params) => (
          <TextField
            label={label ?? t('selectCatalogProduct')}
            placeholder={placeholder}
            value={inputValue ?? sortedOptions.find((option) => option.id === value)?.name ?? ''}
            {...params}
            // InputProps={{
            //   endAdornment: null
            // }}
          />
        )}
        value={sortedOptions.find((option) => option.id === value) ?? null}
        {...props}
      />

      {selectedCatalogProduct && (
        <>
          <CatalogProductDetailsList
            catalogProduct={selectedCatalogProduct}
            showName={false}
            includeMissing={false}
          />
          {/*<Accordion sx={{ boxShadow: 0 }}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ m: '0!important' }}>
              {t('actions:showDetails')}
            </AccordionSummary>
            <AccordionDetails>
              <CatalogProductDetailsList catalogProduct={selectedCatalogProduct} />
            </AccordionDetails>
          </Accordion>*/}
        </>
      )}
    </>
  )
}
